import React, { useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useStores } from '../../../hooks/use-stores';
import TableServer from '../TableServer';
import {
    DELIVERY_STATUS_OPTIONS,
    DeliveryStatusChip,
    TestChip,
    previewJson,
} from './webhookFormatters';

const EVENT_NAME_OPTIONS = [
    'training-completion',
    'assessment-completion',
    'hra-insights-completion',
];

/**
 * The delivery table, used both as the global "Webhook events" page and as the
 * events section of a single webhook's detail page.
 *
 * `fixedWebhookId` is the only difference between the two: when set, the table
 * is scoped to that webhook and the (now meaningless) webhook filter is hidden.
 * Everything else - columns, filters, server paging - is shared, so the two
 * views cannot drift apart.
 */
function WebhookEventsTable({ fixedWebhookId, rowsPerPage = 10 }) {
    const { webhooksStore } = useStores();
    const { events, eventsCount, loadingEvents, eventQuery } = webhooksStore;

    useEffect(() => {
        webhooksStore.resetEventQuery(
            fixedWebhookId ? { webhookId: fixedWebhookId, n: rowsPerPage } : { n: rowsPerPage }
        );
        webhooksStore.loadEvents({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fixedWebhookId]);

    // TableServer fires this on mount as well as on a real page change, so
    // without the guard every table would load twice.
    const onChangePagingParams = useCallback(
        (pageIdx, pageSize) => {
            const { p, n } = webhooksStore.eventQuery;
            if (p === pageIdx && n === pageSize) return;
            webhooksStore.loadEvents({ p: pageIdx, n: pageSize });
        },
        [webhooksStore]
    );

    const applyFilter = (key) => (event) =>
        webhooksStore.loadEvents({ [key]: event.target.value, p: 0 });

    const columns = useMemo(
        () => [
            {
                Header: 'Id',
                accessor: 'id',
                Cell: (x) => (
                    <Link to={`/webhookEvents/${x.value}`}>{x.value}</Link>
                ),
            },
            // The owning webhook lives in webhookData JSON rather than a
            // column, hence the accessor function. Dropped when the table is
            // already scoped to one webhook, where it would repeat the same id
            // on every row.
            ...(fixedWebhookId
                ? []
                : [
                      {
                          Header: 'Webhook',
                          id: 'webhookId',
                          accessor: (row) => row.webhookData && row.webhookData.id,
                          Cell: (x) =>
                              x.value ? (
                                  <Link to={`/webhooks/${x.value}`}>
                                      {x.value}
                                  </Link>
                              ) : (
                                  '-'
                              ),
                          disableSortBy: true,
                      },
                  ]),
            {
                Header: 'Event',
                accessor: 'eventName',
                Cell: (x) => (
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        {x.value}
                        <TestChip isTest={x.row.original.is_test} />
                    </Box>
                ),
            },
            {
                Header: 'Status',
                accessor: 'delivery_status',
                Cell: (x) => <DeliveryStatusChip status={x.value} />,
            },
            {
                Header: 'HTTP',
                accessor: 'status',
                Cell: (x) => (x.value === 0 ? 'no response' : x.value || '-'),
            },
            {
                Header: 'Attempts',
                accessor: 'attempts',
            },
            {
                Header: 'Next try',
                accessor: 'next_try',
                Cell: (x) => (x.value ? moment(x.value).format('LLL') : '-'),
            },
            {
                Header: 'Created',
                accessor: 'created',
                Cell: (x) => (x.value ? moment(x.value).format('LLL') : '-'),
            },
            {
                Header: 'Data',
                accessor: 'definition',
                Cell: (x) => previewJson(x.value && x.value.data),
                disableSortBy: true,
            },
        ],
        // The webhook column appears only in the unscoped table, so the
        // definition has to be rebuilt when the scope changes.
        [fixedWebhookId]
    );

    const pagesTotal = Math.max(
        1,
        Math.ceil(eventsCount / (eventQuery.n || rowsPerPage))
    );

    return (
        <>
            <Box display="flex" style={{ gap: 16 }} mb={2} flexWrap="wrap">
                {!fixedWebhookId && (
                    <TextField
                        label="Webhook id"
                        size="small"
                        value={eventQuery.webhookId}
                        onChange={applyFilter('webhookId')}
                    />
                )}
                <TextField
                    select
                    label="Event"
                    size="small"
                    style={{ minWidth: 200 }}
                    value={eventQuery.eventName}
                    onChange={applyFilter('eventName')}
                >
                    <MenuItem value="">All events</MenuItem>
                    {EVENT_NAME_OPTIONS.map((name) => (
                        <MenuItem key={name} value={name}>
                            {name}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    label="Status"
                    size="small"
                    style={{ minWidth: 160 }}
                    value={eventQuery.deliveryStatus}
                    onChange={applyFilter('deliveryStatus')}
                >
                    <MenuItem value="">Any status</MenuItem>
                    {DELIVERY_STATUS_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    label="Kind"
                    size="small"
                    style={{ minWidth: 160 }}
                    value={eventQuery.isTest}
                    onChange={applyFilter('isTest')}
                >
                    <MenuItem value="">Real and test</MenuItem>
                    <MenuItem value={0}>Real only</MenuItem>
                    <MenuItem value={1}>Test only</MenuItem>
                </TextField>
            </Box>

            <TableServer
                columns={columns}
                data={events.slice()}
                rowsTotal={eventsCount}
                pagesTotal={pagesTotal}
                loadingData={loadingEvents}
                onChangePagingParams={onChangePagingParams}
                initialPageSize={rowsPerPage}
                hidePagination={pagesTotal <= 1}
            />
        </>
    );
}

export default observer(WebhookEventsTable);
